import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toastify } from "../helpers/toast/toastify";
import useApi from "../helpers/apiHelper/requestHelper";
import axios from "axios";
import {
  setRefetchMultiCommunityList,
  setRefetchSpecificUser,
  setRefetchPage,
} from "../redux/multicommunity/multicommunity";

const config = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const useImageUpload = (selectedCommunity) => {
  const api = useApi();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const fileInputRef = useRef(null);

  // State for image upload and cropping
  const [imageSrc, setImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null); // Store original file
  const [showCropper, setShowCropper] = useState(false);
  const [editor, setEditor] = useState(null);
  const [scale, setScale] = useState(1);
  const [fileName, setFileName] = useState("");
  const [showEditChoiceModal, setShowEditChoiceModal] = useState(false);
  const [isProgrammaticUpload, setIsProgrammaticUpload] = useState(false);
  const [isFirstTimeUpload, setIsFirstTimeUpload] = useState(false);

  // File validation
  const validateFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toastify("Please select a valid image file (JPEG, PNG, GIF)", "error");
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toastify("File size must be less than 5MB", "error");
      return false;
    }

    return true;
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validateFile(file)) {
        event.target.value = "";
        setIsProgrammaticUpload(false);
        return;
      }

      // Store the original file
      setOriginalFile(file);
      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  };

  // Handle icon click (plus icon or upload button)
  const handleIconClick = () => {
    if (isProgrammaticUpload) return;

    // Check if there's an existing logo/image
    const hasExistingImage =
      selectedCommunity?.logo || (!user?.leaderCommunity && user?.image);

    if (!hasExistingImage) {
      setIsFirstTimeUpload(true); // Mark as first time upload
      fileInputRef.current?.click();
    } else {
      setIsFirstTimeUpload(false); // Mark as edit
      setShowEditChoiceModal(true);
    }
  };

  // Handle edit choice modal
  const handleEditChoice = async (choice) => {
    setShowEditChoiceModal(false);
    if (choice === "edit") {
      try {
        // Determine which image URL to load
        let imageUrl;
        if (selectedCommunity?._id) {
          // For communities: try original logo first, fallback to logo
          imageUrl = selectedCommunity?.originalLogo || selectedCommunity?.logo;
        } else {
          // For traders/users: try original image first, fallback to image
          imageUrl = user?.originalImage || user?.image;
        }

        if (!imageUrl) {
          toastify("No image found to edit", "error");
          return;
        }

        // Fetch image from backend to avoid CORS issues
        const response = await config.post(
          "community/getImage",
          { imageUrl: imageUrl },
          {
            responseType: "blob", // Important: tell axios to expect binary data
          }
        );

        // Check if response is successful and is actually an image
        if (response.status === 200 && response.data instanceof Blob) {
          // Check content type to ensure it's an image
          const contentType = response.headers["content-type"] || "";
          let blob;
          if (contentType.startsWith("image/")) {
            // It's an image blob - convert to File object
             blob = response.data;
            const file = new File([blob], "original_image.png", {
              type: blob.type || contentType,
            });

            setOriginalFile(file);

            const reader = new FileReader();
            reader.onload = () => {
              setImageSrc(reader.result);
              setShowCropper(true);
            };
            reader.readAsDataURL(blob);
          } else {
            // Response is not an image - might be an error JSON
            const text = await blob.text();
            try {
              const errorData = JSON.parse(text);
              toastify(
                errorData.message || "Unable to load image for editing",
                "error"
              );
            } catch (e) {
              toastify("Unable to load image for editing", "error");
            }
          }
        } else {
          toastify("Unable to load image for editing", "error");
        }
      } catch (error) {
        console.error("Error loading image:", error);

        // Handle error response
        if (error.response) {
          // If error response is a blob (containing JSON error)
          if (error.response.data instanceof Blob) {
            try {
              const errorText = await error.response.data.text();
              const errorData = JSON.parse(errorText);
              toastify(
                errorData.message || "Unable to load image for editing",
                "error"
              );
            } catch (e) {
              toastify("Unable to load image for editing", "error");
            }
          } else if (error.response.data?.message) {
            toastify(error.response.data.message, "error");
          } else {
            toastify("Unable to load image for editing", "error");
          }
        } else {
          toastify("Unable to load image for editing", "error");
        }
      }
    } else if (choice === "upload") {
      setIsProgrammaticUpload(true);
      setIsFirstTimeUpload(false); // Mark as edit when uploading from edit modal
      fileInputRef.current?.click();
    }
  };
  // Handle crop save
  const handleCropSave = async () => {
    if (editor && originalFile) {
      const canvas = editor.getImageScaledToCanvas();
      canvas.toBlob(async (croppedBlob) => {
        setShowCropper(false);

        // Upload both original and cropped images
        await uploadBothImages(
          originalFile,
          croppedBlob,
          fileName || "image.png"
        );

        // Reset states
        setImageSrc(null);
        setOriginalFile(null);
        setIsProgrammaticUpload(false);
        setIsFirstTimeUpload(false);
      }, "image/png");
    }
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setOriginalFile(null);
    setFileName("");
    setIsProgrammaticUpload(false);
    setIsFirstTimeUpload(false);
  };

  // Upload both original and cropped images
  const uploadBothImages = async (originalFile, croppedBlob, fileName) => {
    try {
      // Upload original image
      const originalFormData = new FormData();
      originalFormData.append("file", originalFile);

      const originalResponse = await config.post(
        "community/uploadFile",
        originalFormData
      );
      const originalUrl = originalResponse?.data?.data;

      if (!originalUrl) {
        toastify("Failed to upload original image", "error");
        return;
      }

      // Upload cropped image
      const croppedFormData = new FormData();
      croppedFormData.append("file", croppedBlob, `cropped_${fileName}`);

      const croppedResponse = await config.post(
        "community/uploadFile",
        croppedFormData
      );
      const croppedUrl = croppedResponse?.data?.data;

      if (!croppedUrl) {
        toastify("Failed to upload cropped image", "error");
        return;
      }

      // Update with both URLs
      await updateCommunityLogo(croppedUrl, originalUrl, fileName);
    } catch (error) {
      console.error("Upload error:", error);
      toastify("Failed to upload images. Please try again.", "error");
    }
  };

  // Update community logo with both original and cropped URLs
  const updateCommunityLogo = async (croppedUrl, originalUrl, fileName) => {
    try {
      const response = await api("post", "community/updateCommunityLogo", {
        communityId: selectedCommunity?._id,
        logo: croppedUrl,
        originalLogo: originalUrl, // Add original logo URL
      });

      if (response?.status) {
        setFileName(fileName);
        // Show different toast based on first upload or edit
        const toastMessage = isFirstTimeUpload 
          ? "Image uploaded successfully" 
          : "Image Saved";
        toastify(toastMessage, "success");

        // Trigger refetch to update the community data
        dispatch(setRefetchSpecificUser(true));
        dispatch(setRefetchMultiCommunityList(true));
        dispatch(setRefetchPage("Settings"));
      } else {
        toastify("Failed to update community logo", "error");
      }
    } catch (error) {
      console.error("Logo update error:", error);
      toastify("Failed to update community logo", "error");
    }
  };

  // Reset all states
  const resetStates = () => {
    setImageSrc(null);
    setOriginalFile(null);
    setShowCropper(false);
    setEditor(null);
    setScale(1);
    setFileName("");
    setShowEditChoiceModal(false);
    setIsProgrammaticUpload(false);
    setIsFirstTimeUpload(false);
  };

  return {
    // Refs
    fileInputRef,

    // States
    imageSrc,
    originalFile,
    showCropper,
    editor,
    scale,
    fileName,
    showEditChoiceModal,
    isProgrammaticUpload,

    // Setters
    setEditor,
    setScale,
    setFileName,
    setIsProgrammaticUpload,
    setShowEditChoiceModal,

    // Handlers
    handleFileChange,
    handleIconClick,
    handleEditChoice,
    handleCropSave,
    handleCropCancel,
    resetStates,
  };
};

export default useImageUpload;
